import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const initialState = {
  isLoading: false,
  balance: 0,
  totalDeposits: 0,
  totalSpent: 0,
  transactionsCount: 0,
  transactions: [],
  transactionsLoading: false,
  topUpLoading: false,
  errors: [],
  adminTransactions: { isLoading: false, data: {}, errors: [] },
};

export const getWallet = createAsyncThunk('wallet/getWallet', async (_, thunkApi) => {
  try {
    const { data } = await axios.get('/api/get_wallet', {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const getTransactions = createAsyncThunk('wallet/getTransactions', async (args, thunkApi) => {
  try {
    const { page = 1, size = 10 } = args || {};
    const { data } = await axios.get(`/api/get_transactions?page=${page}&size=${size}`, {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const createTopUp = createAsyncThunk('wallet/createTopUp', async (args, thunkApi) => {
  try {
    const { data } = await axios.post('/api/create_top_up', args, {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const addBalance = createAsyncThunk('wallet/addBalance', async (args, thunkApi) => {
  try {
    const { userId, ...body } = args;
    const { data } = await axios.post(`/api/add_balance/${userId}`, body, {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const getAllTransactions = createAsyncThunk('wallet/getAllTransactions', async (args, thunkApi) => {
  try {
    const { page = 1, size = 10, filter = JSON.stringify({}) } = args || {};
    const { data } = await axios.get(`/api/get_all_transactions?page=${page}&size=${size}&filter=${filter}`, {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const updateTransactionStatus = createAsyncThunk('wallet/updateTransactionStatus', async (args, thunkApi) => {
  try {
    const { id, ...body } = args;
    const { data } = await axios.patch(`/api/update_transaction_status/${id}`, body, {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

const walletSlice = createSlice({
  name: 'wallet',
  initialState,
  extraReducers: {
    [getWallet.pending]: (state) => { state.isLoading = true; state.errors = []; },
    [getWallet.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.balance = action.payload.data.balance;
      state.totalDeposits = action.payload.data.totalDeposits;
      state.totalSpent = action.payload.data.totalSpent;
      state.transactionsCount = action.payload.data.transactionsCount;
    },
    [getWallet.rejected]: (state, action) => { state.isLoading = false; state.errors = action.payload.errors; },

    [getTransactions.pending]: (state) => { state.transactionsLoading = true; },
    [getTransactions.fulfilled]: (state, action) => {
      state.transactionsLoading = false;
      state.transactions = action.payload.data;
    },
    [getTransactions.rejected]: (state) => { state.transactionsLoading = false; },

    [createTopUp.pending]: (state) => { state.topUpLoading = true; state.errors = []; },
    [createTopUp.fulfilled]: (state) => { state.topUpLoading = false; },
    [createTopUp.rejected]: (state, action) => { state.topUpLoading = false; state.errors = action.payload.errors; },

    [addBalance.pending]: (state) => { state.adminTransactions.isLoading = true; state.adminTransactions.errors = []; },
    [addBalance.fulfilled]: (state) => { state.adminTransactions.isLoading = false; },
    [addBalance.rejected]: (state, action) => {
      state.adminTransactions.isLoading = false;
      state.adminTransactions.errors = action.payload.errors;
    },

    [getAllTransactions.pending]: (state) => { state.adminTransactions.isLoading = true; state.adminTransactions.errors = []; },
    [getAllTransactions.fulfilled]: (state, action) => {
      state.adminTransactions.isLoading = false;
      state.adminTransactions.data = action.payload;
    },
    [getAllTransactions.rejected]: (state, action) => {
      state.adminTransactions.isLoading = false;
      state.adminTransactions.data = {};
      state.adminTransactions.errors = action.payload.errors;
    },

    [updateTransactionStatus.pending]: (state) => { state.adminTransactions.isLoading = true; state.adminTransactions.errors = []; },
    [updateTransactionStatus.fulfilled]: (state, action) => {
      state.adminTransactions.isLoading = false;
      const updated = action.payload.data;
      const index = state.adminTransactions.data.data?.findIndex(tx => tx._id === updated._id);
      if (index > -1) state.adminTransactions.data.data[index].status = updated.status;
    },
    [updateTransactionStatus.rejected]: (state, action) => {
      state.adminTransactions.isLoading = false;
      state.adminTransactions.errors = action.payload.errors;
    },
  }
});

export default walletSlice.reducer;
